import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import routes from '../../utils/routes';
import strings from '../../utils/strings';
import { CButton, CInput, CText, CHeader } from '../../components/common';
import { emailValidation } from '../../utils/validation';

const CreatePassword: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [otpError, setOtpError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [step, setStep] = useState<'email' | 'otp' | 'password'>('email');

  const handleSendOTP = () => {
    if (!email) {
      setEmailError(strings.thisFieldIsMandatory);
      return;
    }
    if (!emailValidation(email)) {
      setEmailError(strings.enterValidEmail);
      return;
    }
    setEmailError('');
    setStep('otp');
  };

  const handleVerifyOTP = () => {
    if (!otp) {
      setOtpError(strings.thisFieldIsMandatory);
      return;
    }
    setOtpError('');
    setStep('password');
  };

  const handleResetPassword = () => {
    let isValid = true;

    if (!newPassword) {
      setPasswordError(strings.plsEnterPassword);
      isValid = false;
    } else if (newPassword.length < 8) {
      setPasswordError(strings.enterStrongPassword);
      isValid = false;
    } else {
      setPasswordError('');
    }

    if (!confirmPassword) {
      setConfirmPasswordError(strings.plsEnterPassword);
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      setConfirmPasswordError(strings.confirmPassValidString);
      isValid = false;
    } else {
      setConfirmPasswordError('');
    }

    if (isValid) {
      navigate(routes.successfulPassword);
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <CHeader title={step === 'password' ? strings.createNewPassword : strings.forgotPassWord} />

      <div className="max-w-md mx-auto px-5 py-4">
        {step === 'email' && (
          <>
            <h2
              className="text-2xl font-bold mb-2"
              style={{ color: colors.textColor }}
            >
              {strings.forgotPassWord}
            </h2>
            <CText type="r14" color={colors.grayScale500} style={{ marginBottom: '16px' }}>
              Enter your email address to receive a verification code
            </CText>

            <CInput
              label={strings.email}
              value={email}
              onChange={setEmail}
              placeholder="Enter your email"
              type="email"
              errorText={emailError}
              required
            />

            <CButton title="Send OTP" onPress={handleSendOTP} />

            <div className="text-center mt-4">
              <span
                className="text-sm cursor-pointer"
                style={{ color: colors.primary }}
                onClick={() => navigate(routes.login)}
              >
                {strings.backToSignIn}
              </span>
            </div>
          </>
        )}

        {step === 'otp' && (
          <>
            <h2
              className="text-2xl font-bold mb-2"
              style={{ color: colors.textColor }}
            >
              {strings.otp}
            </h2>
            <CText type="r14" color={colors.grayScale500} style={{ marginBottom: '16px' }}>
              {strings.otpDescription}
            </CText>

            <CInput
              label={strings.otp}
              value={otp}
              onChange={setOtp}
              placeholder="Enter OTP code"
              type="text"
              maxLength={6}
              errorText={otpError}
              required
            />

            <CButton title={strings.verifyOTP} onPress={handleVerifyOTP} />

            <div className="text-center mt-4">
              <CText type="r14" color={colors.grayScale500}>
                Didn't receive code?{' '}
                <span
                  className="cursor-pointer"
                  style={{ color: colors.primary }}
                  onClick={handleSendOTP}
                >
                  {strings.resendOTP}
                </span>
              </CText>
            </div>
          </>
        )}

        {step === 'password' && (
          <>
            <h2
              className="text-2xl font-bold mb-2"
              style={{ color: colors.textColor }}
            >
              {strings.createNewPassword}
            </h2>
            <CText type="r14" color={colors.grayScale500} style={{ marginBottom: '16px' }}>
              {strings.newPasswordDescription}
            </CText>

            <CInput
              label={strings.password}
              value={newPassword}
              onChange={setNewPassword}
              placeholder="Enter new password"
              isSecure
              errorText={passwordError}
              required
            />

            <CInput
              label={strings.confirmPassword}
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder="Confirm new password"
              isSecure
              errorText={confirmPasswordError}
              required
            />

            <CButton title={strings.resetPassword} onPress={handleResetPassword} />
          </>
        )}
      </div>
    </div>
  );
};

export default CreatePassword;
